import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { captureAndReturn } from 'utils/zod/captureAndReturn'

extendZodWithOpenApi(z)

const OverrideIconTypeSchema = z.enum(['bank', 'budget-approval', 'assignees'])
export type OverrideIconType = z.infer<typeof OverrideIconTypeSchema>

export const VisibleIfSchema = z
  .union([
    z.tuple([z.string().uuid()]), // [string]
    z.tuple([]), // []
  ])
  .catch(captureAndReturn('VisibleIfSchema'))

export type VisibleIf = z.infer<typeof VisibleIfSchema>

// 'document' covers both user-uploaded files and scraped URLs — both go through the document extraction pipeline
const AiSourceTypeSchema = z.enum(['document'])

const AiSourceInfoSchema = z.object({
  sourceType: AiSourceTypeSchema.optional(),
  quote: z.string().optional(),
  sourceLabel: z.string(),
  fileStoreId: z.string(),
  sourceUrl: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  sheetName: z.string().optional(),
})

export const AiCompletionConfidenceSchema = z.enum(['high', 'medium', 'low'])
export type AiCompletionConfidence = z.infer<typeof AiCompletionConfidenceSchema>

const AiCompletionReferenceSchema = z.object({
  title: z.string(),
  url: z.string().url(),
})

const AiCompletionMetadataSchema = z
  .object({
    confidence: AiCompletionConfidenceSchema,
    reasoning: z.string(),
    references: z.array(AiCompletionReferenceSchema).optional(),
  })
  .openapi({
    description:
      'Confidence and reasoning for an answer submitted by the AI Form Completion block. Distinct from aiSources, which tracks document-extraction prefill citations.',
  })

export const FormAnswerMetadataSchema = z.object({
  isAiPrefilled: z.boolean().optional(),
  aiSources: z.array(AiSourceInfoSchema).optional(),
  aiCompletionMetadata: AiCompletionMetadataSchema.optional(),
})

export type FormAnswerMetadata = z.infer<typeof FormAnswerMetadataSchema>

// TODO: In wider testing, validate whether this needs to be extended with more properties
//       from either the base question schema or specific question type schemas.
/**
 * Fields that are strictly per-instance — never persisted on the Question Definition. The Form
 * Template's per-question entry always carries them; resolve at form-render time always uses
 * the Form Template's value because the Question Definition doesn't carry one.
 */
export const QuestionInstanceOnlyFieldsSchema = z.object({
  id: z.string(),
})

// TODO: In wider testing, validate whether this needs to be extended with more properties
//       from either the base question schema or specific question type schemas.
/**
 * Fields the Question Definition may carry as a default AND the Form Template's per-question
 * entry may carry as an override.
 */
export const QuestionOverrideableFieldsSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
})

/**
 * The Question-Definition slice of the base schema: everything that lives on the Question
 * Definition (the rest of `BaseFormQuestionSchema` outside of the strictly-per-instance keys),
 * including the overrideable defaults composed in.
 */
export const BaseQuestionDefinitionFieldsSchema = z
  .object({
    name: z.string(),
    visibleIf: VisibleIfSchema,
    isRequired: z.boolean(),
    readOnly: z.boolean().optional(),
    disabled: z.boolean().optional(), // NOTE: This is not set anywhere in the codebase, so I assume it's a legacy field
    weighting: z.number().optional(),
    tooltip: z.string().optional(),
    doubleEntryEnabled: z.boolean().optional(), // TODO(COR-358): Does this need to be in the base schema??
    overrideIconType: OverrideIconTypeSchema.optional(), // TODO(COR-358): Does this need to be in the base schema??
    isScorable: z.boolean().optional(), // Questions in rfx forms can be marked as scorable
    autoFlaggedScoreCategoryIds: z.array(z.string().uuid()).optional(),
    isMaterial: z.boolean().optional(), // Questions marked as material trigger change detection assessment flows
  })
  .merge(QuestionOverrideableFieldsSchema)

/**
 * Link from a Form Template's per-question entry to an Organisation Question. `versionId` is
 * set when the Form Template is pinned to a specific Version; absent means "track latest".
 */
export const OrganisationQuestionLinkSchema = z.object({
  id: z.string().uuid(),
  versionId: z.number().int().positive().optional(),
})
export type OrganisationQuestionLink = z.infer<typeof OrganisationQuestionLinkSchema>

/**
 * The full base Question schema (inline / resolved). `organisationQuestion?` is set by the
 * resolver when the question originated from a Linked catalogue entry — consumers can ignore it
 * and treat the question as if it were authored inline. Note: this field is link metadata, not a
 * Question Definition field; `QuestionConfig` (the persisted definition shape) strips it back out
 * via `QuestionConfigExclusionFieldsSchema` below.
 */
export const BaseFormQuestionSchema = QuestionInstanceOnlyFieldsSchema.merge(BaseQuestionDefinitionFieldsSchema).extend({
  organisationQuestion: OrganisationQuestionLinkSchema.optional(),
})

/**
 * Fields stripped from a resolved `FormQuestion` when projecting down to its `QuestionConfig` —
 * the Question Definition shape persisted on `question_versions.config`. Neither the per-instance
 * `id` nor the resolver-populated `organisationQuestion?` provenance link belongs on the
 * Definition; both are owned by the Form Template's per-question entry, not by the catalogue
 * Question itself.
 *
 * Schema-driven so the type-level `DistributiveOmit` in `QuestionConfig` and the runtime
 * field-stripping in `pickQuestionDefinitionFields` share a single source of truth.
 */
export const QuestionConfigExclusionFieldsSchema = QuestionInstanceOnlyFieldsSchema.extend({
  organisationQuestion: OrganisationQuestionLinkSchema.optional(),
})

/**
 * Field-set a Linked Form Template entry carries excluding the catalogue link itself: the
 * per-instance `id` plus any per-instance overrides of the overrideable Question Definition fields
 * (all optional on the Linked entry; present only when overriding). Pickers extracting the
 * instance subset from a `FormQuestion` parse through this schema so they don't surface definition
 * fields that would then fail the strict union check below.
 */
export const LinkedFormQuestionFieldsSchema = QuestionInstanceOnlyFieldsSchema.merge(QuestionOverrideableFieldsSchema.partial())
export type LinkedFormQuestionFields = z.infer<typeof LinkedFormQuestionFieldsSchema>

/**
 * Stored shape of a Linked Form Template entry, composed from the building blocks that name what
 * each field is for:
 *   - `LinkedFormQuestionFieldsSchema` — instance-only fields + (optional) overrideable overrides.
 *   - `organisationQuestion` — the catalogue link itself.
 *
 * Definition-only fields come from the linked `question_versions.config` at resolve time and are
 * NOT carried here.
 */
export const LinkedFormQuestionSchema = LinkedFormQuestionFieldsSchema.extend({
  organisationQuestion: OrganisationQuestionLinkSchema,
}).strict()
export type LinkedFormQuestion = z.infer<typeof LinkedFormQuestionSchema>

export const BaseFormAnswerSchema = z.object({
  questionId: z.string().nullable(),
  metadata: FormAnswerMetadataSchema.optional(),
})
